import { Grid, GridItem, Text, VStack, Checkbox } from '@chakra-ui/react';

import SelectSearch from 'components/fields/SelectSearch';
import { useAnswerCodes } from '../../hooks';
import { ValidatedField, CurrencyInput } from '..';
import type { CoverageSectionProps } from './types';

const EnvironmentalCoveragesSection = ({
  coverages,
  updateCoverage,
  isLoadingCodes,
}: CoverageSectionProps) => {
  const { getOptions } = useAnswerCodes();
  const coveragesEntity = { coverages } as unknown as Record<string, unknown>;

  return (
    <Grid templateColumns="1fr 1fr" gap={4}>
      <GridItem>
        <Text fontWeight="700" mb={3}>
          Environmental Coverages
        </Text>
        <VStack align="stretch" spacing={2}>
          <Checkbox
            isChecked={coverages.earthquake_enabled}
            onChange={(e) =>
              updateCoverage('earthquake_enabled', e.target.checked)
            }
            colorScheme="blue"
          >
            <Text fontSize="sm">Earthquake - Building</Text>
          </Checkbox>
          <Checkbox
            isChecked={coverages.earthquake_condo_enabled}
            onChange={(e) =>
              updateCoverage('earthquake_condo_enabled', e.target.checked)
            }
            colorScheme="blue"
          >
            <Text fontSize="sm">Earthquake - Contents</Text>
          </Checkbox>
          <Checkbox
            isChecked={coverages.collapse_enabled}
            onChange={(e) =>
              updateCoverage('collapse_enabled', e.target.checked)
            }
            colorScheme="blue"
          >
            <Text fontSize="sm">Collapse</Text>
          </Checkbox>
          <Checkbox
            isChecked={coverages.mass_evacuation_enabled}
            onChange={(e) =>
              updateCoverage('mass_evacuation_enabled', e.target.checked)
            }
            colorScheme="blue"
          >
            <Text fontSize="sm">Mass Evacuation</Text>
          </Checkbox>
        </VStack>
      </GridItem>
      <GridItem>
        <ValidatedField
          itemPath="location/coverages"
          fieldPath="coverages.environmental_deductible_cad"
          label="Deductible"
          labelSize="xs"
          entity={coveragesEntity}
          formControlProps={{ mb: 3 }}
        >
          <CurrencyInput
            value={coverages.environmental_deductible_cad ?? ''}
            onChange={(_, value) =>
              updateCoverage('environmental_deductible_cad', value)
            }
            placeholder="BASE"
          />
        </ValidatedField>
        <VStack align="stretch" spacing={2}>
          <ValidatedField
            itemPath="location/coverages"
            fieldPath="coverages.earthquake_limit_cad"
            label="Earthquake - Building Limit"
            labelSize="xs"
            entity={coveragesEntity}
            isDisabled={!coverages.earthquake_enabled}
          >
            <CurrencyInput
              value={coverages.earthquake_limit_cad ?? ''}
              onChange={(_, value) =>
                updateCoverage('earthquake_limit_cad', value)
              }
              placeholder={coverages.earthquake_enabled ? 'BASE' : ''}
              isDisabled={!coverages.earthquake_enabled}
            />
          </ValidatedField>
          <ValidatedField
            itemPath="location/coverages"
            fieldPath="coverages.earthquake_coverage_option"
            label="Earthquake Coverage Option"
            labelSize="xs"
            entity={coveragesEntity}
            isDisabled={
              !coverages.earthquake_enabled &&
              !coverages.earthquake_condo_enabled
            }
          >
            <SelectSearch
              options={getOptions('earthquake_coverage_option')}
              value={coverages.earthquake_coverage_option || undefined}
              onChange={(value) =>
                updateCoverage(
                  'earthquake_coverage_option',
                  (value as string) || '',
                )
              }
              isLoading={isLoadingCodes}
              isDisabled={
                !coverages.earthquake_enabled &&
                !coverages.earthquake_condo_enabled
              }
              allowClear
            />
          </ValidatedField>
          <ValidatedField
            itemPath="location/coverages"
            fieldPath="coverages.earthquake_condo_limit_cad"
            label="Earthquake - Contents Limit"
            labelSize="xs"
            entity={coveragesEntity}
            isDisabled={!coverages.earthquake_condo_enabled}
          >
            <CurrencyInput
              value={coverages.earthquake_condo_limit_cad ?? ''}
              onChange={(_, value) =>
                updateCoverage('earthquake_condo_limit_cad', value)
              }
              placeholder={coverages.earthquake_condo_enabled ? 'BASE' : ''}
              isDisabled={!coverages.earthquake_condo_enabled}
            />
          </ValidatedField>
          <ValidatedField
            itemPath="location/coverages"
            fieldPath="coverages.collapse_limit_cad"
            label="Collapse Limit"
            labelSize="xs"
            entity={coveragesEntity}
            isDisabled={!coverages.collapse_enabled}
          >
            <CurrencyInput
              value={coverages.collapse_limit_cad ?? ''}
              onChange={(_, value) =>
                updateCoverage('collapse_limit_cad', value)
              }
              placeholder={coverages.collapse_enabled ? 'BASE' : ''}
              isDisabled={!coverages.collapse_enabled}
            />
          </ValidatedField>
          <ValidatedField
            itemPath="location/coverages"
            fieldPath="coverages.mass_evacuation_limit_cad"
            label="Mass Evacuation Limit"
            labelSize="xs"
            entity={coveragesEntity}
            isDisabled={!coverages.mass_evacuation_enabled}
          >
            <CurrencyInput
              value={coverages.mass_evacuation_limit_cad ?? ''}
              onChange={(_, value) =>
                updateCoverage('mass_evacuation_limit_cad', value)
              }
              placeholder={coverages.mass_evacuation_enabled ? 'BASE' : ''}
              isDisabled={!coverages.mass_evacuation_enabled}
            />
          </ValidatedField>
        </VStack>
      </GridItem>
    </Grid>
  );
};

export default EnvironmentalCoveragesSection;
